/**
 * Live visibility channel between the grid's scrolling row and the header's
 * fan tiles.
 *
 * The grid row scrolls continuously (free momentum, not paging), and the fan
 * tiles want to answer "which tables are on screen right now?" with a dot per
 * visible table (Jason 2026-08-17). Threading scroll positions through React
 * as props would re-render the header at scroll frequency; this module is the
 * same pattern as tableSwipeDrag — subscribers write what they hear straight
 * to their own DOM nodes, no React work per frame.
 *
 * What travels is each table's VISIBLE FRACTION (0..1), not a boolean. The
 * fraction is what makes the dots fluid: as a table slides off the edge its
 * dot fades in proportion rather than blinking out at some threshold, so the
 * dots move with the scroll the way the felt does.
 */

export type GridVisibility = Record<string, number>;

type Listener = (fractions: GridVisibility) => void;

let current: GridVisibility = {};
const listeners = new Set<Listener>();

export const publishGridVisibility = (fractions: GridVisibility): void => {
  current = fractions;
  listeners.forEach((l) => l(fractions));
};

/** The grid unmounted — nothing is visible, tell everyone to stand down. */
export const clearGridVisibility = (): void => publishGridVisibility({});

export const getGridVisibility = (): GridVisibility => current;

export const subscribeGridVisibility = (listener: Listener): (() => void) => {
  listeners.add(listener);
  // Late subscribers (the header remounts on focus changes) get the current
  // state immediately rather than waiting for the next scroll.
  listener(current);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * How much of [left, right] lies inside the viewport [viewLeft, viewRight],
 * as a fraction of the cell's own width.
 */
export const visibleFraction = (
  left: number,
  right: number,
  viewLeft: number,
  viewRight: number
): number => {
  const width = right - left;
  if (width <= 0) return 0;
  const overlap = Math.min(right, viewRight) - Math.max(left, viewLeft);
  return Math.min(1, Math.max(0, overlap / width));
};

/**
 * FIFO queue of tables waiting on the player's action.
 *
 * The grid auto-scrolls to the table that needs the player, and when several
 * need them at once it must not ping-pong: the one that asked FIRST holds the
 * view until it is resolved (action taken or timed out), then the next in
 * line gets its turn (Jason 2026-08-17, agreed with Ryan and Victor).
 *
 * Pure so the ordering rule is testable: survivors keep their existing order,
 * newcomers append in the order given.
 */
export const advanceActionQueue = (previous: string[], nowActing: string[]): string[] => {
  const acting = new Set(nowActing);
  const kept = previous.filter((id) => acting.has(id));
  const known = new Set(kept);
  const added = nowActing.filter((id) => !known.has(id));
  return [...kept, ...added];
};

/**
 * Which table in the queue actually gets the view.
 *
 * FIFO decides, with one exception: a table waiting on a multi-run VOTE jumps
 * ahead of one merely waiting on a turn (EN-10237). The window is a handful of
 * seconds, the chips are already committed, and there is no fold-by-default
 * that leaves the player roughly where they expected to be — a turn that runs
 * out just folds a hand they were probably folding anyway.
 *
 * The jump does NOT reorder the queue, so it cannot starve anyone: between two
 * votes the earlier one still holds the view until it resolves, exactly as two
 * turns do.
 */
export const actionQueueHead = (queue: string[], voting: ReadonlySet<string>): string | undefined =>
  queue.find((id) => voting.has(id)) ?? queue[0];
